import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Ticket, Plus, Inbox, UserCog, Loader2, Search,
} from "lucide-react";
import RaiseTicketModal from "./RaiseTicketModal";
import TicketDetailModal from "./TicketDetailModal";
import { formatSlaLabel, slaToneClass } from "./slaHelpers";

const TABS = [
  { key: "raised", label: "Raised by Me", icon: Ticket, testid: "tickets-tab-raised" },
  { key: "tagged", label: "Tagged Me", icon: Inbox, testid: "tickets-tab-tagged" },
  { key: "escalated", label: "Escalated to Me", icon: UserCog, testid: "tickets-tab-escalated" },
];

const STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "open", label: "Open" },
  { key: "in_progress", label: "In Progress" },
  { key: "resolved", label: "Resolved" },
];

const PRIORITY_TONE = {
  high:   "border-red-500/40 text-red-400 bg-red-500/5",
  medium: "border-[#d4af37]/40 text-[#d4af37] bg-[#d4af37]/5",
  low:    "border-gray-500/40 text-gray-400 bg-gray-500/5",
};
const STATUS_TONE = {
  open:        "border-red-500/40 text-red-400 bg-red-500/5",
  in_progress: "border-[#d4af37]/40 text-[#d4af37] bg-[#d4af37]/5",
  resolved:    "border-emerald-500/40 text-emerald-400 bg-emerald-500/5",
};

export default function TicketsPage() {
  const { principal } = useAuth();
  const [tab, setTab] = useState("raised");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({ raised: 0, tagged: 0, escalated: 0 });
  const [overdueInScope, setOverdueInScope] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showRaise, setShowRaise] = useState(false);
  const [openTicketId, setOpenTicketId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ scope: tab });
      if (statusFilter !== "all") params.append("status", statusFilter);
      const { data } = await api.get(`/tickets?${params.toString()}`);
      setTickets(data.tickets || []);
      setCounts(data.counts || { raised: 0, tagged: 0, escalated: 0 });
      setOverdueInScope(data.overdue || 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [tab, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);
  // Re-render every 60s so SLA countdown labels stay live even without
  // hitting the API.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setNowTick((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return tickets;
    return tickets.filter((t) =>
      (t.title || "").toLowerCase().includes(term)
      || (t.description || "").toLowerCase().includes(term)
      || (t.created_by_name || "").toLowerCase().includes(term)
      || (t.project?.project_name || "").toLowerCase().includes(term)
      || (t.project?.client_name || "").toLowerCase().includes(term)
    );
  }, [tickets, q]);

  const onRaised = () => { setShowRaise(false); load(); toast.success("Ticket raised"); };
  const onTicketChanged = () => { load(); };

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-6 md:px-10 py-6 bg-[#050505]/80 sticky top-0 z-30">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">
              Internal · Tickets
            </p>
            <h1 className="font-display text-2xl md:text-3xl text-white mt-1">
              Support &amp; Escalation Desk
            </h1>
          </div>
          <button onClick={() => setShowRaise(true)}
                  className="iz-btn-primary flex items-center gap-2"
                  data-testid="raise-ticket-btn">
            <Plus className="w-4 h-4"/> Raise Ticket
          </button>
        </div>
      </div>

      <div className="px-6 md:px-10 py-6 space-y-4">
        <div className="iz-card p-2 flex flex-wrap gap-1" data-testid="tickets-tabs">
          {TABS.map((t) => {
            const showOverdue = t.key === "escalated" && tab === "escalated" && overdueInScope > 0;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                      className={`px-4 py-2 rounded-md text-xs font-mono uppercase tracking-widest flex items-center gap-2 transition-colors ${
                        tab === t.key
                          ? "bg-[#d4af37] text-black"
                          : "text-gray-400 hover:text-white hover:bg-[#111]"
                      }`}
                      data-testid={t.testid}>
                <t.icon className="w-3.5 h-3.5"/>{t.label}
                <span className={`text-[10px] font-normal ${tab === t.key ? "text-black/70" : "text-gray-500"}`}>
                  {counts[t.key] ?? 0}
                </span>
                {showOverdue && (
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full border border-red-500/70 text-red-100 bg-red-500/30 animate-pulse"
                        data-testid="tickets-overdue-badge">
                    {overdueInScope} overdue
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 flex-wrap" data-testid="tickets-status-filter">
            {STATUS_TABS.map((s) => (
              <button key={s.key} onClick={() => setStatusFilter(s.key)}
                      className={`text-[11px] font-mono uppercase tracking-widest px-2.5 py-1 rounded border transition-colors ${
                        statusFilter === s.key
                          ? "border-[#d4af37] text-[#d4af37] bg-[#d4af37]/10"
                          : "border-[#262626] text-gray-500 hover:text-white"
                      }`}
                      data-testid={`tickets-status-${s.key}`}>
                {s.label}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2 border border-[#262626] rounded-md px-2 py-1 bg-[#0a0a0a] w-full sm:w-72">
            <Search className="w-3.5 h-3.5 text-gray-600"/>
            <input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search…"
                   className="bg-transparent outline-none text-xs text-white w-full"
                   data-testid="tickets-search"/>
          </div>
        </div>

        {loading ? (
          <div className="iz-card p-8 flex items-center justify-center gap-2 text-gray-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin"/> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="iz-card p-10 text-center text-sm text-gray-500 italic"
               data-testid="tickets-empty">
            {tab === "raised" && "You haven’t raised any tickets yet. Click "}
            {tab === "tagged" && "You’re not tagged on any tickets. "}
            {tab === "escalated" && "Nothing escalated your way. "}
            {tab === "raised" && (
              <button className="text-[#d4af37] underline" onClick={() => setShowRaise(true)}>
                raise your first ticket
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2" data-testid="tickets-list">
            {filtered.map((t) => {
              const slaLabel = formatSlaLabel(t);
              const slaTone = slaToneClass(t);
              const overdue = t.is_overdue && t.status !== "resolved";
              return (
              <button key={t.id} onClick={() => setOpenTicketId(t.id)}
                      className={`w-full text-left iz-card p-4 transition-colors ${
                        overdue
                          ? "border-red-500/50 hover:border-red-400"
                          : "hover:border-[#d4af37]/40"
                      }`}
                      data-testid={`ticket-row-${t.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{t.title}</p>
                    {t.project?.client_name && (
                      <p className="text-[11px] text-gray-500 mt-0.5 truncate">
                        {t.project.client_name}
                        {t.project.project_name ? ` · ${t.project.project_name}` : ""}
                      </p>
                    )}
                    {t.description && (
                      <p className="text-[11px] text-gray-400 mt-1 line-clamp-2">
                        {t.description}
                      </p>
                    )}
                    <p className="text-[10px] text-gray-600 mt-1.5">
                      by {t.created_by_name || "—"} · {new Date(t.updated_at).toLocaleString()}
                      {t.comments?.length > 0 ? ` · ${t.comments.length} comment${t.comments.length === 1 ? "" : "s"}` : ""}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${STATUS_TONE[t.status] || ""}`}>
                      {t.status?.replace(/_/g, " ")}
                    </span>
                    <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${PRIORITY_TONE[t.priority] || ""}`}>
                      {t.priority} priority
                    </span>
                    {slaLabel && (
                      <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${slaTone}`}
                            data-testid={`ticket-sla-${t.id}`}>
                        {slaLabel}
                      </span>
                    )}
                  </div>
                </div>
              </button>
              );
            })}
          </div>
        )}
      </div>

      {showRaise && (
        <RaiseTicketModal onClose={() => setShowRaise(false)} onCreated={onRaised}/>
      )}
      {openTicketId && (
        <TicketDetailModal ticketId={openTicketId}
                           currentUserId={principal?.id}
                           onClose={() => setOpenTicketId(null)}
                           onChanged={onTicketChanged}/>
      )}
    </div>
  );
}
